import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminConfirmDialog,
  AdminField,
  AdminInput,
  AdminModal,
  AdminSelect,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
  AdminTabs,
  AdminToastRegion,
  type AdminToast,
} from './AdminUi'
import { type ProductFamily } from '../../shared/productFamily'
import '../styles/admin-weekly-menu.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type MenuSlot = 'live' | 'current-draft' | 'next-draft'
type DraftSlot = 'current-draft' | 'next-draft'

type Flavour = {
  id: string
  name: string
  productFamily: ProductFamily
  description: string
  imagePath: string
  active: boolean
  soldOut: boolean
  sortOrder: number | null
}

type MenuSet = {
  slot: MenuSlot
  week: { startsOn: string; endsOn: string; timeZone: 'Pacific/Auckland' }
  publishedAt: string | null
  updatedAt: string
  brownie: Flavour[]
  stuffed: Flavour[]
  brownies: Flavour[]
}

type WeeklyMenuState = {
  timeZone: 'Pacific/Auckland'
  operationalWeek: MenuSet['week']
  limits: { brownie: number; stuffed: number; brownies: number; total: number }
  catalogue: Flavour[]
  live: MenuSet
  currentDraft: MenuSet
  nextDraft: MenuSet
}

type ContentMediaProduct = {
  id: string
  staticImage: string
  assignment: null | {
    draftMediaId: string | null
  }
}

type ContentMediaSnapshot = {
  products: ContentMediaProduct[]
}

type TabId = 'this-week' | 'next-week' | 'catalogue'
type PublishTarget = { slot: DraftSlot; menu: MenuSet; earlyPublication: boolean } | null
type NextDraftAction = 'carry-over' | 'start-fresh' | null
type NewFlavourDraft = {
  name: string
  productFamily: ProductFamily
  description: string
}

const EMPTY_NEW_FLAVOUR: NewFlavourDraft = {
  name: '',
  productFamily: 'Massive Stuffed Cookie',
  description: '',
}

const DATE = new Intl.DateTimeFormat('en-NZ', {
  timeZone: 'Pacific/Auckland',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

function parseDateOnly(value: string) {
  const date = new Date(`${value}T12:00:00+12:00`)
  return Number.isNaN(date.getTime()) ? value : DATE.format(date)
}

function weekLabel(menu: MenuSet) {
  return `${parseDateOnly(menu.week.startsOn)} – ${parseDateOnly(menu.week.endsOn)}`
}

function isFutureOperationalWeek(menu: MenuSet, currentOperationalWeek: MenuSet['week']) {
  return menu.week.startsOn > currentOperationalWeek.startsOn
}

function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}

function ids(items: Flavour[]) { return items.map((item) => item.id) }
function sameIds(a: Flavour[], b: Flavour[]) { return ids(a).join('|') === ids(b).join('|') }
function stuffedClass(flavour: Flavour) { return flavour.productFamily === 'Massive Stuffed Cookie' ? 'is-stuffed' : '' }

function withManagedDraftImages(menu: WeeklyMenuState, contentMedia: ContentMediaSnapshot) {
  const imageByProductId = new Map(contentMedia.products.map((product) => [
    product.id,
    product.assignment?.draftMediaId
      ? `/api/admin/content-media/media/${encodeURIComponent(product.assignment.draftMediaId)}/file`
      : product.staticImage,
  ]))

  const patchFlavour = (flavour: Flavour): Flavour => ({
    ...flavour,
    imagePath: imageByProductId.get(flavour.id) ?? flavour.imagePath,
  })

  const patchSet = (set: MenuSet): MenuSet => ({
    ...set,
    brownie: set.brownie.map(patchFlavour),
    stuffed: set.stuffed.map(patchFlavour),
    brownies: set.brownies.map(patchFlavour),
  })

  return {
    ...menu,
    catalogue: menu.catalogue.map(patchFlavour),
    live: patchSet(menu.live),
    currentDraft: patchSet(menu.currentDraft),
    nextDraft: patchSet(menu.nextDraft),
  }
}

function MenuFlavourRow({
  flavour,
  index,
  count,
  onMove,
  onRemove,
  onAvailabilityChange,
  disabled,
  availabilityDisabled,
}: {
  flavour: Flavour
  index: number
  count: number
  onMove: (direction: -1 | 1) => void
  onRemove: () => void
  onAvailabilityChange: () => void
  disabled: boolean
  availabilityDisabled: boolean
}) {
  return (
    <article className={`admin-weekly-menu__selected-item ${flavour.active ? '' : 'is-inactive'} ${flavour.soldOut ? 'is-sold-out' : ''} ${stuffedClass(flavour)}`.trim()}>
      <div className="admin-weekly-menu__thumb"><img src={flavour.imagePath} alt="" aria-hidden="true" /></div>
      <div className="admin-weekly-menu__selected-copy">
        <strong>{flavour.name}</strong>
        <span>{flavour.productFamily}</span>
        {!flavour.active ? <AdminStatusPill tone="warning">Inactive</AdminStatusPill> : null}
        {flavour.soldOut ? <AdminStatusPill tone="pink">SOLD OUT THIS WEEK</AdminStatusPill> : null}
      </div>
      <div className="admin-weekly-menu__row-actions">
        <AdminButton
          compact
          variant={flavour.soldOut ? 'secondary' : 'ghost'}
          type="button"
          disabled={availabilityDisabled}
          onClick={onAvailabilityChange}
        >{flavour.soldOut ? 'Mark available' : 'Mark sold out'}</AdminButton>
        <AdminButton compact variant="ghost" type="button" disabled={disabled || index === 0} onClick={() => onMove(-1)} aria-label={`Move ${flavour.name} up`}>↑</AdminButton>
        <AdminButton compact variant="ghost" type="button" disabled={disabled || index === count - 1} onClick={() => onMove(1)} aria-label={`Move ${flavour.name} down`}>↓</AdminButton>
        <AdminButton compact variant="secondary" type="button" disabled={disabled} onClick={onRemove}>Remove</AdminButton>
      </div>
    </article>
  )
}

function FamilyEditor({
  title,
  family,
  selected,
  catalogue,
  limit,
  totalSelected,
  totalLimit,
  slot,
  disabled,
  availabilityDisabled,
  onChange,
  onAvailabilityChange,
}: {
  title: string
  family: ProductFamily
  selected: Flavour[]
  catalogue: Flavour[]
  limit: number
  totalSelected: number
  totalLimit: number
  slot: DraftSlot
  disabled: boolean
  availabilityDisabled: boolean
  onChange: (next: Flavour[]) => void
  onAvailabilityChange: (slot: DraftSlot, flavour: Flavour) => void
}) {
  const selectedIds = new Set(selected.map((item) => item.id))
  const available = catalogue.filter((item) => item.productFamily === family && item.active && !selectedIds.has(item.id))

  function move(index: number, direction: -1 | 1) {
    const target = index + direction
    if (target < 0 || target >= selected.length) return
    const next = [...selected]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  return (
    <section className="admin-weekly-menu__family">
      <div className="admin-weekly-menu__family-heading">
        <div><h3>{title}</h3><p>Choose up to {limit} products. Weekly menu total: {totalSelected} / {totalLimit}.</p></div>
        <strong>{selected.length} / {limit}</strong>
      </div>
      <div className="admin-weekly-menu__selected-list">
        {selected.length ? selected.map((flavour, index) => (
          <MenuFlavourRow
            key={flavour.id}
            flavour={flavour}
            index={index}
            count={selected.length}
            disabled={disabled}
            availabilityDisabled={availabilityDisabled}
            onMove={(direction) => move(index, direction)}
            onRemove={() => onChange(selected.filter((item) => item.id !== flavour.id))}
            onAvailabilityChange={() => onAvailabilityChange(slot, flavour)}
          />
        )) : <div className="admin-weekly-menu__empty-family">No flavours selected.</div>}
      </div>
      <div className="admin-weekly-menu__add-grid">
        {available.map((flavour) => (
          <button
            key={flavour.id}
            type="button"
            className={`admin-weekly-menu__add-card ${stuffedClass(flavour)}`.trim()}
            disabled={disabled || selected.length >= limit || totalSelected >= totalLimit}
            onClick={() => onChange([...selected, flavour])}
          >
            <img src={flavour.imagePath} alt="" aria-hidden="true" />
            <span><strong>{flavour.name}</strong><small>Add flavour</small></span>
          </button>
        ))}
        {!available.length ? <span className="admin-weekly-menu__all-used">No other active flavours available.</span> : null}
      </div>
    </section>
  )
}

function PreviewGroup({ title, items, numbered = false }: { title: string; items: Flavour[]; numbered?: boolean }) {
  return (
    <section className="admin-weekly-menu__preview-group">
      <div><h3>{title}</h3><span>{items.length} / 4</span></div>
      {items.length ? (
        <ol>
          {items.map((item, index) => (
            <li className={stuffedClass(item)} key={item.id}>
              <img src={item.imagePath} alt="" aria-hidden="true" />
              <span>
                <strong>{numbered ? `${index + 1}. ` : ''}{item.name}</strong>
                {item.soldOut ? <small className="admin-weekly-menu__preview-sold-out">SOLD OUT THIS WEEK</small> : null}
                <small>{item.description}</small>
              </span>
            </li>
          ))}
        </ol>
      ) : <p>No flavours selected.</p>}
    </section>
  )
}

export default function AdminWeeklyMenuPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [state, setState] = useState<WeeklyMenuState | null>(null)
  const [tab, setTab] = useState<TabId>('this-week')
  const [currentBrownie, setCurrentBrownie] = useState<Flavour[]>([])
  const [currentStuffed, setCurrentStuffed] = useState<Flavour[]>([])
  const [currentBrownies, setCurrentBrownies] = useState<Flavour[]>([])
  const [nextBrownie, setNextBrownie] = useState<Flavour[]>([])
  const [nextStuffed, setNextStuffed] = useState<Flavour[]>([])
  const [nextBrownies, setNextBrownies] = useState<Flavour[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [preview, setPreview] = useState<MenuSet | null>(null)
  const [publishTarget, setPublishTarget] = useState<PublishTarget>(null)
  const [inactiveTarget, setInactiveTarget] = useState<Flavour | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Flavour | null>(null)
  const [nextDraftAction, setNextDraftAction] = useState<NextDraftAction>(null)
  const [createFlavourOpen, setCreateFlavourOpen] = useState(false)
  const [newFlavour, setNewFlavour] = useState<NewFlavourDraft>(EMPTY_NEW_FLAVOUR)
  const [toasts, setToasts] = useState<AdminToast[]>([])
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  function sync(next: WeeklyMenuState) {
    setState(next)
    setCurrentBrownie(next.currentDraft.brownie)
    setCurrentStuffed(next.currentDraft.stuffed)
    setCurrentBrownies(next.currentDraft.brownies)
    setNextBrownie(next.nextDraft.brownie)
    setNextStuffed(next.nextDraft.stuffed)
    setNextBrownies(next.nextDraft.brownies)
  }

  function pushToast(title: string, message?: string, tone: AdminToast['tone'] = 'success') {
    const id = crypto.randomUUID()
    setToasts([{ id, title, message, tone }])
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4_000)
  }

  async function load() {
    setBusy(true)
    setError('')
    try {
      const [sessionResponse, menuResponse, contentMediaResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin' }),
        fetch('/api/admin/weekly-menu', { credentials: 'same-origin', cache: 'no-store' }),
        fetch('/api/admin/content-media', { credentials: 'same-origin', cache: 'no-store' }),
      ])
      if (sessionResponse.status === 401 || menuResponse.status === 401 || contentMediaResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/weekly-menu')
        return
      }
      if (!sessionResponse.ok || !menuResponse.ok) throw new Error('Weekly Menu could not be loaded.')
      setSession(await sessionResponse.json() as SessionPayload)
      const menu = await menuResponse.json() as WeeklyMenuState
      if (contentMediaResponse.ok) {
        const contentMedia = await contentMediaResponse.json() as ContentMediaSnapshot
        sync(withManagedDraftImages(menu, contentMedia))
      } else {
        sync(menu)
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Weekly Menu could not be loaded.')
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => { void load() }, [])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    try {
      const response = await fetch('/api/admin/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-CSRF-Token': session.csrfToken },
      })
      if (!response.ok) throw new Error()
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed.')
      setLogoutBusy(false)
    }
  }

  async function post<T = Record<string, unknown>>(path: string, body?: Record<string, unknown>) {
    if (!session) throw new Error('Admin session unavailable.')
    const response = await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-CSRF-Token': session.csrfToken, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    })
    const payload = await response.json().catch(() => null) as (T & { message?: string }) | null
    if (response.status === 401) {
      window.location.replace('/admin/login?next=/admin/weekly-menu')
      throw new Error('Session expired.')
    }
    if (!response.ok) throw new Error(payload?.message || 'Weekly Menu update failed.')
    return payload as T
  }

  async function saveDraft(slot: DraftSlot, brownie: Flavour[], stuffed: Flavour[], brownies: Flavour[]) {
    setBusy(true)
    setError('')
    try {
      await post(`/api/admin/weekly-menu/drafts/${slot}`, {
        brownieIds: ids(brownie),
        stuffedIds: ids(stuffed),
        brownieProductIds: ids(brownies),
      })
      await load()
      pushToast('Private draft saved', slot === 'current-draft' ? 'The live customer menu has not changed.' : 'Next Week remains private until you publish it.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Draft could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  async function carryOver() {
    setNextDraftAction(null)
    setBusy(true)
    setError('')
    try {
      await post('/api/admin/weekly-menu/next-draft/carry-over')
      await load()
      pushToast('Current menu carried over', 'Next Week remains private.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Next Week could not be updated.')
    } finally {
      setBusy(false)
    }
  }

  async function startFresh() {
    setNextDraftAction(null)
    setBusy(true)
    setError('')
    try {
      await post('/api/admin/weekly-menu/next-draft/start-fresh')
      await load()
      pushToast('Next Week cleared', 'The private draft is now empty.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Next Week could not be cleared.')
    } finally {
      setBusy(false)
    }
  }

  async function createFlavour() {
    if (busy) return
    const name = newFlavour.name.trim()
    const description = newFlavour.description.trim()
    if (!name || !description) {
      setError('Add a flavour name and crave/description copy before creating the product.')
      return
    }

    setBusy(true)
    setError('')
    try {
      const result = await post<{
        created: true
        product: { id: string; name: string; productFamily: ProductFamily; active: false }
      }>('/api/admin/weekly-menu/catalogue', {
        name,
        productFamily: newFlavour.productFamily,
        description,
      })
      setCreateFlavourOpen(false)
      setNewFlavour(EMPTY_NEW_FLAVOUR)
      await load()
      setTab('catalogue')
      pushToast(
        'New flavour created',
        `${result.product.name} (${result.product.id}) is inactive. Add its approved image/details in Content & Media, then activate it here when ready.`,
      )
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The new flavour could not be created.')
    } finally {
      setBusy(false)
    }
  }

  async function toggleActive(flavour: Flavour, active: boolean) {
    setInactiveTarget(null)
    setBusy(true)
    setError('')
    try {
      await post(`/api/admin/weekly-menu/catalogue/${encodeURIComponent(flavour.id)}/active`, { active })
      await load()
      pushToast(
        active ? 'Flavour activated' : 'Flavour made inactive',
        active ? `${flavour.name} can be selected into drafts.` : `${flavour.name} remains in catalogue/history but cannot be newly published.`,
      )
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Catalogue could not be updated.'
      setError(message)
      pushToast(
        active ? 'Activation blocked' : 'Catalogue update blocked',
        message,
        'danger',
      )
    } finally {
      setBusy(false)
    }
  }

  async function deleteFlavour(flavour: Flavour) {
    setDeleteTarget(null)
    setBusy(true)
    setError('')
    try {
      await post<{ deleted: true; productId: string; retainedMediaIds: string[] }>(
        `/api/admin/content-media/products/${encodeURIComponent(flavour.id)}/delete`,
        {},
      )
      await load()
      pushToast('Flavour deleted', `${flavour.name} was removed from the catalogue. Any managed media was retained in Content & Media.`)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The flavour could not be deleted.')
    } finally {
      setBusy(false)
    }
  }

  async function toggleSoldOut(slot: DraftSlot, flavour: Flavour) {
    setBusy(true)
    setError('')
    try {
      await post(`/api/admin/weekly-menu/availability/${slot}/${encodeURIComponent(flavour.id)}`, { soldOut: !flavour.soldOut })
      await load()
      pushToast(
        flavour.soldOut ? 'Flavour marked available' : 'Flavour sold out',
        flavour.soldOut
          ? `${flavour.name} can be selected again.`
          : `${flavour.name} remains visible but cannot be ordered from this weekly menu.`,
      )
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Weekly availability could not be updated.')
    } finally {
      setBusy(false)
    }
  }

  async function publish() {
    if (!publishTarget) return
    const target = publishTarget
    setPublishTarget(null)
    setBusy(true)
    setError('')
    try {
      const requestBody: Record<string, unknown> = { slot: target.slot }
      if (target.earlyPublication) {
        requestBody.confirmEarlyPublication = true
        requestBody.expectedWeekStartsOn = target.menu.week.startsOn
        requestBody.expectedWeekEndsOn = target.menu.week.endsOn
      }
      await post('/api/admin/weekly-menu/publish', requestBody)
      await load()
      pushToast('Menu published', 'The customer-facing live menu has been replaced successfully.')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Menu could not be published.')
    } finally {
      setBusy(false)
    }
  }

  if (error && (!session || !state)) {
    return (
      <main className="admin-shell-gate-error">
        <AdminStatePanel state="error" title="Weekly Menu unavailable" description={error} action={<AdminButton onClick={() => void load()}>Retry</AdminButton>} />
      </main>
    )
  }

  if (!session || !state) {
    return <main className="admin-shell-gate-loading"><AdminCard><AdminSkeleton lines={8} /></AdminCard></main>
  }

  const menuState = state
  const currentChanged = !sameIds(currentBrownie, menuState.live.brownie)
    || !sameIds(currentStuffed, menuState.live.stuffed)
    || !sameIds(currentBrownies, menuState.live.brownies)

  function editor(
    slot: DraftSlot,
    menu: MenuSet,
    brownie: Flavour[],
    stuffed: Flavour[],
    brownies: Flavour[],
    setBrownie: (value: Flavour[]) => void,
    setStuffed: (value: Flavour[]) => void,
    setBrownies: (value: Flavour[]) => void,
  ) {
    const isNext = slot === 'next-draft'
    const dirty = !sameIds(brownie, menu.brownie)
      || !sameIds(stuffed, menu.stuffed)
      || !sameIds(brownies, menu.brownies)
    const allSelected = [...brownie, ...stuffed, ...brownies]
    const totalSelected = allSelected.length
    const hasInactive = allSelected.some((item) => !item.active)
    const previewMenu: MenuSet = { ...menu, brownie, stuffed, brownies }
    const nextHasSelections = isNext && (
      totalSelected > 0
      || menu.brownie.length + menu.stuffed.length + menu.brownies.length > 0
    )
    const publishHasChanges = isNext || currentChanged

    return (
      <div className="admin-weekly-menu__editor">
        <AdminCard className="admin-weekly-menu__menu-card">
          <div className="admin-weekly-menu__menu-heading">
            <div>
              <div className="admin-weekly-menu__status-line">
                <AdminStatusPill tone={isNext ? 'info' : 'success'}>{isNext ? 'Private draft' : 'Live menu workspace'}</AdminStatusPill>
                {dirty ? <AdminStatusPill tone="warning">Unsaved edits</AdminStatusPill> : null}
                {!isNext && currentChanged ? <AdminStatusPill tone="pink">Unpublished changes</AdminStatusPill> : null}
              </div>
              <h2>{isNext ? 'Next Week' : 'This Week'}</h2>
              <p>{weekLabel(menu)} · Pacific/Auckland</p>
            </div>
            <div className="admin-weekly-menu__heading-actions">
              {isNext ? (
                <>
                  <AdminButton
                    variant="secondary"
                    type="button"
                    disabled={busy}
                    onClick={() => { if (nextHasSelections) setNextDraftAction('carry-over'); else void carryOver() }}
                  >Carry over current</AdminButton>
                  <AdminButton
                    variant="ghost"
                    type="button"
                    disabled={busy || !nextHasSelections}
                    onClick={() => setNextDraftAction('start-fresh')}
                  >Start fresh</AdminButton>
                </>
              ) : null}
              <AdminButton variant="secondary" type="button" onClick={() => setPreview(previewMenu)}>Preview customer menu</AdminButton>
            </div>
          </div>

          {hasInactive ? <div className="admin-weekly-menu__warning">An inactive flavour is still present in this draft. Remove it or reactivate it before publishing.</div> : null}
          {dirty ? <div className="admin-weekly-menu__availability-note">Save selection/order edits before changing sold-out availability.</div> : null}

          <div className="admin-weekly-menu__family-grid">
            <FamilyEditor
              title="Brownie Cookie Sandwich"
              family="Brownie Cookie Sandwich"
              selected={brownie}
              catalogue={menuState.catalogue}
              limit={menuState.limits.brownie}
              totalSelected={totalSelected}
              totalLimit={menuState.limits.total}
              slot={slot}
              disabled={busy}
              availabilityDisabled={busy || dirty}
              onChange={setBrownie}
              onAvailabilityChange={(targetSlot, flavour) => void toggleSoldOut(targetSlot, flavour)}
            />
            <FamilyEditor
              title="Massive Stuffed Cookie"
              family="Massive Stuffed Cookie"
              selected={stuffed}
              catalogue={menuState.catalogue}
              limit={menuState.limits.stuffed}
              totalSelected={totalSelected}
              totalLimit={menuState.limits.total}
              slot={slot}
              disabled={busy}
              availabilityDisabled={busy || dirty}
              onChange={setStuffed}
              onAvailabilityChange={(targetSlot, flavour) => void toggleSoldOut(targetSlot, flavour)}
            />
            <FamilyEditor
              title="Brownie"
              family="Brownie"
              selected={brownies}
              catalogue={menuState.catalogue}
              limit={menuState.limits.brownies}
              totalSelected={totalSelected}
              totalLimit={menuState.limits.total}
              slot={slot}
              disabled={busy}
              availabilityDisabled={busy || dirty}
              onChange={setBrownies}
              onAvailabilityChange={(targetSlot, flavour) => void toggleSoldOut(targetSlot, flavour)}
            />
          </div>

          <div className="admin-weekly-menu__publish-bar">
            <div>
              <strong>Draft changes stay private until Publish menu succeeds.</strong>
              <span>Store Status controls whether customers can order; Weekly Menu controls which flavours are published.</span>
            </div>
            <div>
              <AdminButton variant="secondary" type="button" disabled={busy || !dirty} onClick={() => void saveDraft(slot, brownie, stuffed, brownies)}>Save draft</AdminButton>
              <AdminButton
                type="button"
                disabled={busy || dirty || hasInactive || !publishHasChanges}
                onClick={() => setPublishTarget({
                  slot,
                  menu: previewMenu,
                  earlyPublication: isFutureOperationalWeek(previewMenu, menuState.operationalWeek),
                })}
              >Publish menu</AdminButton>
            </div>
          </div>
        </AdminCard>
      </div>
    )
  }

  return (
    <AdminShell
      title="Weekly Menu"
      eyebrow="OPERATIONS"
      description="Manage approved flavours, prepare private drafts and deliberately publish the customer-facing weekly menu."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={<AdminButton variant="secondary" disabled={busy} onClick={() => void load()}>Refresh menu</AdminButton>}
    >
      <div className="admin-weekly-menu-page">
        {error ? <div className="admin-weekly-menu__error" role="alert">{error}</div> : null}

        <section className="admin-weekly-menu__summary">
          <div><span>Live week</span><strong>{weekLabel(menuState.live)}</strong><small>{menuState.live.brownie.length + menuState.live.stuffed.length + menuState.live.brownies.length} / {menuState.limits.total} published products</small></div>
          <div><span>Brownie Cookie Sandwich</span><strong>{menuState.live.brownie.length} / {menuState.limits.brownie}</strong><small>Published live</small></div>
          <div><span>Massive Stuffed Cookie</span><strong>{menuState.live.stuffed.length} / {menuState.limits.stuffed}</strong><small>Published live</small></div>
          <div><span>Brownie</span><strong>{menuState.live.brownies.length} / {menuState.limits.brownies}</strong><small>Published live</small></div>
          <div><span>Catalogue</span><strong>{menuState.catalogue.filter((item) => item.active).length} active</strong><small>{menuState.catalogue.length} approved products total</small></div>
        </section>

        <AdminTabs
          idBase="weekly-menu"
          activeId={tab}
          onChange={(id) => setTab(id as TabId)}
          tabs={[
            { id: 'this-week', label: 'This Week', badge: menuState.live.brownie.length + menuState.live.stuffed.length + menuState.live.brownies.length },
            { id: 'next-week', label: 'Next Week', badge: menuState.nextDraft.brownie.length + menuState.nextDraft.stuffed.length + menuState.nextDraft.brownies.length },
            { id: 'catalogue', label: 'Catalogue', badge: menuState.catalogue.length },
          ]}
        />

        {tab === 'this-week' ? editor('current-draft', menuState.currentDraft, currentBrownie, currentStuffed, currentBrownies, setCurrentBrownie, setCurrentStuffed, setCurrentBrownies) : null}
        {tab === 'next-week' ? editor('next-draft', menuState.nextDraft, nextBrownie, nextStuffed, nextBrownies, setNextBrownie, setNextStuffed, setNextBrownies) : null}

        {tab === 'catalogue' ? (
          <AdminCard className="admin-weekly-menu__catalogue-card">
            <div className="admin-weekly-menu__catalogue-heading">
              <div>
                <h2>Approved flavour catalogue</h2>
                <p>Active means a flavour is eligible to be selected into a weekly menu. Inactive flavours remain in catalogue/history.</p>
              </div>
              <div className="admin-weekly-menu__catalogue-heading-actions">
                <AdminStatusPill tone="neutral">{menuState.catalogue.length} catalogue flavours</AdminStatusPill>
                <AdminButton type="button" disabled={busy} onClick={() => setCreateFlavourOpen(true)}>Add new flavour</AdminButton>
              </div>
            </div>
            <div className="admin-weekly-menu__catalogue-grid">
              {menuState.catalogue.map((flavour) => (
                <article className={`admin-weekly-menu__catalogue-item ${flavour.active ? '' : 'is-inactive'} ${stuffedClass(flavour)}`.trim()} key={flavour.id}>
                  <div className="admin-weekly-menu__catalogue-image"><img src={flavour.imagePath} alt={flavour.name} /></div>
                  <div className="admin-weekly-menu__catalogue-copy">
                    <div><h3>{flavour.name}</h3><span>{flavour.productFamily}</span></div>
                    <p>{flavour.description}</p>
                  </div>
                  <div className="admin-weekly-menu__catalogue-action">
                    <AdminStatusPill tone={flavour.active ? 'success' : 'neutral'}>{flavour.active ? 'Active' : 'Inactive'}</AdminStatusPill>
                    {flavour.active ? (
                      <AdminButton variant="secondary" compact disabled={busy} onClick={() => setInactiveTarget(flavour)}>Make inactive</AdminButton>
                    ) : (
                      <>
                        <AdminButton compact disabled={busy} onClick={() => void toggleActive(flavour, true)}>Activate</AdminButton>
                        <AdminButton variant="ghost" compact disabled={busy} onClick={() => setDeleteTarget(flavour)}>Delete</AdminButton>
                      </>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </AdminCard>
        ) : null}

        <AdminModal
          open={createFlavourOpen}
          title="Add new flavour"
          description="Create a new permanent catalogue identity. Existing flavours and their history are not overwritten."
          onClose={() => { if (!busy) { setCreateFlavourOpen(false); setNewFlavour(EMPTY_NEW_FLAVOUR) } }}
          footer={(
            <>
              <AdminButton
                variant="secondary"
                type="button"
                disabled={busy}
                onClick={() => { setCreateFlavourOpen(false); setNewFlavour(EMPTY_NEW_FLAVOUR) }}
              >Cancel</AdminButton>
              <AdminButton
                type="button"
                disabled={busy || !newFlavour.name.trim() || !newFlavour.description.trim()}
                onClick={() => void createFlavour()}
              >Create inactive flavour</AdminButton>
            </>
          )}
        >
          <div className="admin-weekly-menu__create-flavour">
            <div className="admin-weekly-menu__create-note">
              New flavours start <strong>inactive</strong>. After creation, go to Content & Media to add the approved product image and any Flavour Detail information. Activate it here only when the product is ready to use.
            </div>
            <AdminField label="Flavour name" required hint="Use the customer-facing flavour name.">
              <AdminInput
                value={newFlavour.name}
                maxLength={80}
                autoComplete="off"
                onChange={(event) => setNewFlavour((current) => ({ ...current, name: event.target.value }))}
                placeholder="e.g. S'mores"
              />
            </AdminField>
            <AdminField label="Product family" required>
              <AdminSelect
                value={newFlavour.productFamily}
                onChange={(event) => setNewFlavour((current) => ({ ...current, productFamily: event.target.value as ProductFamily }))}
              >
                <option value="Brownie Cookie Sandwich">Brownie Cookie Sandwich</option>
                <option value="Massive Stuffed Cookie">Massive Stuffed Cookie</option>
                <option value="Brownie">Brownie</option>
              </AdminSelect>
            </AdminField>
            <AdminField label="Crave / description copy" required hint="This becomes the existing short product description and can be edited later in Content & Media.">
              <textarea
                className="admin-ui-input admin-weekly-menu__create-description"
                value={newFlavour.description}
                maxLength={220}
                onChange={(event) => setNewFlavour((current) => ({ ...current, description: event.target.value }))}
                placeholder="A thick cookie stuffed with..."
                rows={4}
              />
            </AdminField>
            {newFlavour.productFamily === 'Massive Stuffed Cookie' ? (
              <div className="admin-weekly-menu__create-note">Massive Stuffed Cookie will start with the locked approximate weight of <strong>140 g</strong>. Other product families start with weight blank.</div>
            ) : null}
          </div>
        </AdminModal>

        <AdminModal
          open={Boolean(preview)}
          title="Preview customer menu"
          description={preview ? `${weekLabel(preview)} · Private preview only` : undefined}
          onClose={() => setPreview(null)}
        >
          {preview ? (
            <div className="admin-weekly-menu__preview">
              <PreviewGroup title="Brownie Cookie Sandwich" items={preview.brownie} />
              <PreviewGroup title="Massive Stuffed Cookie" items={preview.stuffed} />
              <PreviewGroup title="Brownie" items={preview.brownies} />
              <p>Opening this preview does not alter the live customer menu.</p>
            </div>
          ) : null}
        </AdminModal>

        <AdminModal
          open={Boolean(publishTarget)}
          title={publishTarget?.earlyPublication ? 'Publish Next Week early?' : 'Publish menu?'}
          description={publishTarget
            ? publishTarget.earlyPublication
              ? weekLabel(publishTarget.menu)
              : `${weekLabel(publishTarget.menu)} · This will replace the customer-facing live menu.`
            : undefined}
          onClose={() => setPublishTarget(null)}
          footer={(
            <>
              <AdminButton variant="secondary" onClick={() => setPublishTarget(null)}>Cancel</AdminButton>
              <AdminButton onClick={() => void publish()}>{publishTarget?.earlyPublication ? 'Publish early' : 'Publish menu'}</AdminButton>
            </>
          )}
        >
          {publishTarget ? (
            <div className="admin-weekly-menu__publish-confirm">
              {publishTarget.earlyPublication ? (
                <div className="admin-weekly-menu__warning">
                  <strong>This menu is scheduled for a future week.</strong>{' '}
                  Publishing it now will <strong>immediately replace the menu customers currently see</strong>. It will not wait until {parseDateOnly(publishTarget.menu.week.startsOn)}.
                </div>
              ) : (
                <div className="admin-weekly-menu__warning"><strong>High-impact change.</strong> Customers will see this exact selection and ordering after publication succeeds.</div>
              )}
              <PreviewGroup title="Brownie Cookie Sandwich" items={publishTarget.menu.brownie} numbered />
              <PreviewGroup title="Massive Stuffed Cookie" items={publishTarget.menu.stuffed} numbered />
              <PreviewGroup title="Brownie" items={publishTarget.menu.brownies} numbered />
            </div>
          ) : null}
        </AdminModal>

        <AdminConfirmDialog
          open={Boolean(inactiveTarget)}
          title={inactiveTarget ? `Make ${inactiveTarget.name} inactive?` : 'Make flavour inactive?'}
          description={inactiveTarget ? 'This is allowed only after the flavour has been removed from the Live menu, This Week draft and Next Week draft. If any menu still references it, the change will be blocked.' : ''}
          confirmLabel="Make inactive"
          onCancel={() => setInactiveTarget(null)}
          onConfirm={() => { if (inactiveTarget) void toggleActive(inactiveTarget, false) }}
        />

        <AdminConfirmDialog
          open={Boolean(deleteTarget)}
          title={deleteTarget ? `Delete ${deleteTarget.name} permanently?` : 'Delete flavour permanently?'}
          description={deleteTarget ? 'This removes the inactive flavour from the catalogue and Product Images. It is blocked if Live, This Week draft or Next Week draft still references it. Managed media is retained in Content & Media. This cannot be undone.' : ''}
          confirmLabel="Delete flavour"
          dangerous
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => { if (deleteTarget) void deleteFlavour(deleteTarget) }}
        />

        <AdminConfirmDialog
          open={nextDraftAction === 'start-fresh'}
          title="Clear the Next Week draft?"
          description="This will remove every current selection from the private Next Week draft. The live customer menu will not change."
          confirmLabel="Start fresh"
          onCancel={() => setNextDraftAction(null)}
          onConfirm={() => void startFresh()}
        />

        <AdminConfirmDialog
          open={nextDraftAction === 'carry-over'}
          title="Replace the Next Week draft?"
          description="This will replace the existing Next Week selections with the current live menu. The result remains private until you publish it."
          confirmLabel="Carry over current"
          onCancel={() => setNextDraftAction(null)}
          onConfirm={() => void carryOver()}
        />

        <AdminToastRegion toasts={toasts} onDismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))} />
      </div>
    </AdminShell>
  )
}
